/* eslint-disable no-restricted-imports */
import * as H from 'history';
import React from 'react';
import {
  ExtractRouteParams,
  Redirect,
  RedirectProps,
  Route,
  RouteProps,
  generatePath,
  useHistory,
} from 'react-router';
import { Link, LinkProps } from 'react-router-dom';

type RoutePathsToObj<T extends string> = {
  [K in T]: never;
};

/**
 * Da sovrascrivere nei singoli progetti, es:
 *
 * declare module '@framework/ui' {
 *  export interface BrandedRouterPathsInterface extends RoutePathsToObj<RoutePaths> {}
 * }
 */
export interface BrandedRouterPathsInterface {}

export type BrandedRouterPaths = keyof BrandedRouterPathsInterface;

interface TypedRouteProps extends RouteProps {
  path?: BrandedRouterPaths;
}

interface TypedRedirectProps extends RedirectProps {
  from?: BrandedRouterPaths;
  to: BrandedRouterPaths | TypedLocationDescriptor;
}

type TypedLocationDescriptor = H.LocationDescriptorObject & {
  pathname?: BrandedRouterPaths;
};

type TypedLocationTo =
  | BrandedRouterPaths
  | TypedLocationDescriptor
  | BrandedPath;

interface TypedLinkProps extends LinkProps {
  /**
   * È possibile passare un oggetto con la proprietà pathname o direttamente
   * un path tra quelli censiti.
   * Per gli URL dinamici, usare `generatePath` per generare il path.
   */
  to: TypedLocationTo;
  disabled?: boolean | null;
}

export type BrandedPath = BrandedRouterPaths & { __typedPath: true };

/**
 * Tipizzazione forte per la route.
 */
export function TypedRoute(props: TypedRouteProps) {
  return <Route {...props} />;
}

export function TypedRedirect(props: TypedRedirectProps) {
  return <Redirect {...props} />;
}

export const typedGeneratePath = <T extends BrandedRouterPaths>(
  path: T,
  params?: ExtractRouteParams<T, string>,
  queryString?: string,
) => (generatePath(path, params as any) + (queryString ?? '')) as BrandedPath;

export function TypedLink(props: TypedLinkProps) {
  if (props.disabled) {
    return (
      <a {...props} href="">
        {props.children}
      </a>
    );
  }
  return <Link {...props} />;
}

const typedUseHistory = useHistory as <TState>(this: void) => Omit<
  H.History<TState>,
  'push' | 'location' | 'replace'
> & {
  location: H.Location<TState> & {
    pathname: BrandedRouterPaths | BrandedPath;
  };
  push(location: TypedLocationTo, state?: TState): void;
  replace(location: TypedLocationTo, state?: TState): void;
};

export {
  TypedLink as Link,
  TypedRedirect as Redirect,
  TypedRoute as Route,
  typedGeneratePath as generatePath,
  typedUseHistory as useHistory,
};
export type {
  TypedLocationDescriptor as LocationDescriptor,
  TypedLocationTo as LocationTo,
};

/**
 * Crea una versione tipizzata di Route, Redirect e Link in base
 * ai path passati.
 */
export function getTypedReactRouter() {
  return {
    Route: TypedRoute,
    Redirect: TypedRedirect,
    Link: TypedLink,
    generatePath: typedGeneratePath,
    useHistory: typedUseHistory,
  };
}
